<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/customers">Customers</a></li>
        <li class="breadcrumb-item active">{{ isEditMode ? 'Edit Customer' : 'Add Customer' }}</li>
      </ol>
    </nav>
    <h1 class="h2 mb-0 fw-bold">{{ isEditMode ? 'Edit Customer' : 'Add Customer' }}</h1>
  </div>
</div>

<form [formGroup]="form" (ngSubmit)="onSubmit()">
  @if (saveError) {
    <div class="alert alert-danger" role="alert">{{ saveError }}</div>
  }
  <div class="row g-4">
    <!-- Left Column - Main Form -->
    <div class="col-lg-8">
      <!-- Basic Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-person me-2"></i>Basic Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label">Customer Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control" formControlName="name"
                     placeholder="e.g., Johnson Family Farms"
                     [class.is-invalid]="isFieldInvalid('name')">
              <div class="invalid-feedback">Please enter a customer name (at least 2 characters)</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Contact Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-telephone me-2"></i>Contact Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Email</label>
              <input type="email" class="form-control" formControlName="email"
                     placeholder="e.g., contact@example.com"
                     [class.is-invalid]="isFieldInvalid('email')">
              <div class="invalid-feedback">Please enter a valid email address</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Phone</label>
              <input type="tel" class="form-control" formControlName="phone"
                     mask="(000) 000-0000"
                     [validation]="false"
                     placeholder="e.g., (555) 123-4567">
            </div>
          </div>
        </div>
      </div>

      <!-- Address -->
      <div class="card mb-4">
        <div class="card-header p-3 d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-geo-alt me-2"></i>Address</h5>
          <button type="button" class="btn btn-outline-secondary btn-sm"
                  (click)="openInMaps()"
                  [disabled]="!form.get('address')?.value && !form.get('city')?.value"
                  title="View on map">
            <i class="bi bi-map me-1"></i>View Map
          </button>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label">Street Address</label>
              <input type="text" class="form-control" formControlName="address"
                     placeholder="e.g., 1234 Farm Road">
            </div>
            <div class="col-md-5">
              <label class="form-label">City</label>
              <input type="text" class="form-control" formControlName="city"
                     placeholder="e.g., Dallas">
            </div>
            <div class="col-md-4">
              <label class="form-label">State</label>
              <select class="form-select" formControlName="state">
                <option value="">Select state...</option>
                <option *ngFor="let state of states" [value]="state">{{ state }}</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label">ZIP Code</label>
              <input type="text" class="form-control" formControlName="zip"
                     placeholder="e.g., 75201">
            </div>
          </div>
        </div>
      </div>

      <!-- Notes -->
      <div class="card mb-4 mb-lg-0">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-chat-left-text me-2"></i>Notes</h5>
        </div>
        <div class="card-body">
          <textarea appCharCounter class="form-control" formControlName="notes" rows="3"
                    placeholder="Any additional notes about this customer..."></textarea>
        </div>
      </div>
    </div>

    <!-- Right Column - Sidebar -->
    <div class="col-lg-4">
      <div class="sticky-top sticky-sidebar">
      <!-- Tips Card -->
      <div class="card mb-4">
        <div class="card-body">
          <h6 class="d-flex align-items-center gap-2 mb-3">
            <i class="bi bi-lightbulb text-warning"></i>
            Tips
          </h6>
          <ul class="small text-muted mb-0 ps-3">
            <li class="mb-2">Customers are the foundation for organizing your fields and spray records</li>
            <li class="mb-2">Add contact information to easily reach customers about spray schedules</li>
            <li class="mb-2">Use notes for special instructions or preferences</li>
            <li>After adding a customer, you can assign fields to them</li>
          </ul>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0">Summary</h5>
        </div>
        <div class="card-body">
          <div class="summary-item" *ngIf="form.get('name')?.value">
            <div class="summary-label text-muted">Name</div>
            <div class="summary-value">{{ form.get('name')?.value }}</div>
          </div>
          <div class="summary-item" *ngIf="form.get('email')?.value">
            <div class="summary-label text-muted">Email</div>
            <div class="summary-value">{{ form.get('email')?.value }}</div>
          </div>
          <div class="summary-item" *ngIf="form.get('phone')?.value">
            <div class="summary-label text-muted">Phone</div>
            <div class="summary-value">{{ form.get('phone')?.value | phoneFormat }}</div>
          </div>
          <div class="summary-item" *ngIf="form.get('city')?.value || form.get('state')?.value">
            <div class="summary-label text-muted">Location</div>
            <div class="summary-value">
              {{ form.get('city')?.value }}<span *ngIf="form.get('city')?.value && form.get('state')?.value">, </span>{{ form.get('state')?.value }}
            </div>
          </div>

          <hr class="my-3">

          <div class="d-grid gap-2">
            <button type="submit" class="btn btn-primary" [disabled]="isSaving">
              <ng-container *ngIf="!isSaving">
                <i class="bi bi-check-lg me-2"></i>{{ isEditMode ? 'Update Customer' : 'Add Customer' }}
              </ng-container>
              <ng-container *ngIf="isSaving">
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Saving...
              </ng-container>
            </button>
            <button type="button" class="btn btn-outline-secondary" (click)="onCancel()">
              Cancel
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</form>
